/**
 * Shared SWR configuration + helpers.
 *
 * Why SWR (not React Query)?  SWR is ~4 KB gzipped and we only need 1 % of
 * its surface area — request deduplication + a stale-while-revalidate window
 * for the heaviest GET on the public side (`/api/series?lite=1`). The full
 * React Query store would be overkill for that.
 *
 * Why only the catalog page-1 fetch?  The catalog mounts inside StrictMode
 * and several effects (filter debounce, scroll-restore, hot-cache hydrate)
 * can independently trigger an initial `/api/series` fetch. Without dedup
 * each public-catalog visitor was firing the same page-1 URL 4-5 times on
 * first paint. SWR collapses those into one network call and serves the
 * follow-ups from its in-memory cache for 30 s.
 */
import { useEffect, useRef } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";

// Generic fetcher backed by our axios instance — keeps the auth header + 401
// redirect interceptor intact. Returns the parsed JSON body so SWR consumers
// don't need to know about axios at all.
const swrFetcher = (url) => api.get(url).then((r) => r.data);

// 30 s stale window — matches the server-side hot cache TTL so the two layers
// reinforce each other instead of fighting.
const STALE_MS = 30_000;

/**
 * useDedupedGet(url) — thin wrapper around `useSWR` with sane defaults for our
 * read-only public list endpoints:
 *  • `dedupingInterval`: 30 000 ms — identical keys within this window share a
 *    single in-flight request and reuse the cached response. This is the
 *    requested "staleTime of 30 seconds" behaviour.
 *  • `revalidateOnFocus: false` — the public catalog is read-mostly and we
 *    don't want to re-hit the API every time the user alt-tabs.
 *  • `revalidateOnReconnect: true` — kept on so a phone returning from
 *    a tunnel/lock-screen refreshes prices once it's back online.
 *  • `errorRetryCount: 2` — Cloudflare 5xx is rare but not zero; one retry
 *    keeps us robust without retry-storms.
 *  • `shouldRetryOnError` is left default-true.
 *
 * Pass `null` as `url` to skip the fetch (e.g. while the param dependencies
 * are still resolving).
 */
export function useDedupedGet(url) {
  return useSWR(url, swrFetcher, {
    dedupingInterval: STALE_MS,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 2,
    keepPreviousData: true,
  });
}

/**
 * Build a query-string for an /api/series request in a stable, sorted form so
 * the SWR cache key is deterministic regardless of which order the consumer
 * passed params in. (`{q:'a',sort:'name_asc'}` and `{sort:'name_asc',q:'a'}`
 * MUST hit the same cache entry — otherwise dedupe accomplishes nothing.)
 */
export function seriesKey(params) {
  if (!params) return null;
  const cleaned = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .sort(([a], [b]) => (a < b ? -1 : 1));
  if (cleaned.length === 0) return "/series";
  const qs = cleaned
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return `/series?${qs}`;
}

/**
 * Tracks whether SWR's `data` is still the previous filter's payload (because
 * `keepPreviousData` is on). Consumers can show a subtle "refreshing" hint
 * while the new filter is fetching, without blanking the grid in between.
 */
export function useIsRefreshing(isValidating, isLoading) {
  const wasValidating = useRef(false);
  useEffect(() => { wasValidating.current = isValidating; }, [isValidating]);
  return isValidating && !isLoading;
}
